import { labelForStep, Step } from "~/lib/pipeline";
import { Job } from "~/components/build/Show/lib/types";

/**
 * Server-side ordering options. Mirrors `BuildShow::OrderBy` on the backend.
 *
 * Entry collection keys encode filter + order: e.g., `steps:running:position`.
 *
 * Duration-based ordering (`run_time`, `wait_time`) is intentionally omitted
 * for now: a running job's duration changes every second, which would
 * destabilise BTree sort keys and server pagination cursors mid-build.
 * Reintroduce only with a strategy for live builds (e.g., gate on
 * `build.finishedAt`).
 */
type OrderBy = "position" | "name";

/**
 * Composite sort key as an array of comparable strings.
 * Compared element-by-element: first element is the primary sort,
 * subsequent elements are tiebreakers (e.g., position).
 */
type SortKey = string[];

/**
 * Compare two composite sort keys element-by-element.
 * Returns negative if left < right, positive if left > right, 0 if equal.
 */
function compareSortKeys(left: SortKey, right: SortKey): number {
  const len = Math.max(left.length, right.length);
  for (let idx = 0; idx < len; idx++) {
    const lv = left[idx];
    const rv = right[idx];

    // Missing elements sort before present ones
    if (lv === undefined && rv === undefined) {
      return 0;
    }
    if (lv === undefined) {
      return -1;
    }
    if (rv === undefined) {
      return 1;
    }

    if (lv < rv) {
      return -1;
    }
    if (lv > rv) {
      return 1;
    }
  }
  return 0;
}

/**
 * Extract the OrderBy segment from an entry collection key.
 * Key format: `steps:{filter}:{orderBy}` or `jobs:{scope}:{orderBy}`
 */
function extractOrderBy(key: string): OrderBy {
  const segments = key.split(":");
  const orderBy = segments[segments.length - 1];
  if (orderBy === "name") {
    return "name";
  }
  return "position";
}

/**
 * Compute a composite sort key for a step based on the active order-by mode.
 *
 * Collections are always stored in ASC order. DESC is `.reverse()` at the view
 * layer. The default sort is `{ column: "position", direction: "ASC" }`.
 *
 * `name` includes position as a tiebreaker for stable cursor pagination.
 * Uses `labelForStep` so command steps fall back to the command script (and
 * block/input steps to "Continue") — mirrors what the user sees in the list.
 *
 *   position  → [position]
 *   name      → [label, position]
 */
function computeStepSortKey(step: Step, orderBy: OrderBy = "position"): SortKey {
  const position = step.sortKey || step.uuid;

  switch (orderBy) {
    case "name":
      return [labelForStep(step).toLowerCase(), position];
    case "position":
    default:
      return [position];
  }
}

/**
 * Compute a composite sort key for a job based on the active order-by mode.
 *
 * Same conventions as step sort keys — always ASC, position tiebreaker.
 * The position tiebreaker is the backend-provided unique `sortKey` (falling
 * back to `id`); `createdAt` is not unique within a build (batch inserts
 * collide sub-millisecond) and would cause BTree key collisions.
 *
 *   position  → [position]
 *   name      → [name, position]
 */
function computeJobSortKey(job: Job, orderBy: OrderBy = "position"): SortKey {
  const position = job.sortKey || job.id;

  switch (orderBy) {
    case "name": {
      const name = "name" in job && typeof job.name === "string" ? job.name : "";
      return [name.toLowerCase(), position];
    }
    case "position":
    default:
      return [position];
  }
}

export { compareSortKeys, extractOrderBy, computeStepSortKey, computeJobSortKey };
export type { OrderBy, SortKey };
